import { styled } from '@linaria/react';
import { useEffect, useMemo, useState } from 'react';
import { Measure, ModuleType } from '../../../api/work-periods-client/work-periods-client.type';
import { BrandedLoadingOverlay } from '../../../components/loader/branded-loader';
import { useWorkPeriodStore } from '../../../store/work-period-store/work-period-store';
import { useFlora } from '../../flora/flora.context';
import { getSprintComparisonFloraContext } from '../../../helpers/process-analysis-shared/process-analysis-shared.helpers';
import {
  useSprintAvailableTargets,
  useSprintComparisonViewData,
} from '../../../helpers/process-analysis-shared/process-analysis-shared.hooks';
import type { TaskRecord } from '../../tasks-table/tasks-table.type';
import { NoWorkPeriodSelected } from '../components/no-work-period-selected';
import { useWorkPeriodsTasksData } from '../hooks/work-periods-calculation.hooks';
import { WorkPeriodsTab } from '../work-periods.type';
import { ComparisonMeasureComparison } from './comparison-measure-comparison';
import { ComparisonMeasuresOverTime } from './comparison-measures-over-time';
import { ComparisonTable } from './components/comparison-table';

type Props = {
  onTasksChange: (tasks: TaskRecord[], title?: string, isLoading?: boolean) => void;
};

export function ComparisonView({ onTasksChange }: Props) {
  const { selectedWorkPeriods, selectedTab } = useWorkPeriodStore();
  const reversedWorkPeriods = useMemo(() => [...selectedWorkPeriods].reverse(), [selectedWorkPeriods]);
  const { setContextualInfo } = useFlora();

  const { customMeasures, measureData, isFetching, isLoading, hasWorkPeriods } =
    useSprintComparisonViewData(selectedWorkPeriods);
  const availableTargets = useSprintAvailableTargets(selectedWorkPeriods);

  const [taskQuery, setTaskQuery] = useState<{
    filter: string;
    title: string;
    workPeriodId: string;
    measure: string;
  } | null>(null);

  const { data: tasksQueryData = [], isFetching: isTasksFetching } = useWorkPeriodsTasksData(
    [taskQuery?.measure as Measure],
    taskQuery?.filter,
    { enabled: !!taskQuery },
    {
      work_period_id: taskQuery?.workPeriodId || '',
      measure_name: [taskQuery?.measure as Measure],
      measure_filter: taskQuery?.filter,
      is_custom: true,
      module_type: ModuleType.WorkPeriod,
    },
  );

  // Effect to trigger onTasksChange when tasks are loading
  useEffect(() => {
    if (taskQuery && isTasksFetching && onTasksChange) {
      const title = `${taskQuery.title}`;
      onTasksChange([], title, true);
    }
  }, [taskQuery, isTasksFetching, onTasksChange]);

  // Effect to trigger onTasksChange when tasks data is loaded
  useEffect(() => {
    if (taskQuery && !isTasksFetching && onTasksChange) {
      const title = `${taskQuery.title}`;
      onTasksChange(tasksQueryData as TaskRecord[], title, false);
    }
  }, [tasksQueryData, taskQuery, isTasksFetching, onTasksChange]);

  useEffect(() => {
    if (reversedWorkPeriods) {
      setContextualInfo(getSprintComparisonFloraContext(reversedWorkPeriods));
    }
  }, [reversedWorkPeriods, setContextualInfo]);

  const handleMeasureClick = (measure: string, workPeriodName: string, title: string, date?: string | null) => {
    const workPeriod = reversedWorkPeriods.find((wp) => wp.name === workPeriodName);
    setTaskQuery({
      filter: date ? date : '',
      title: title,
      workPeriodId: workPeriod?.id || '',
      measure: measure as Measure,
    });
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: ignore handleMeasureClick
  const mainComponent = useMemo(() => {
    switch (selectedTab) {
      case WorkPeriodsTab.COMPARISON_TABLE:
        return (
          <ComparisonTable
            customMeasures={customMeasures}
            measureData={measureData}
            workPeriods={reversedWorkPeriods}
          />
        );
      case WorkPeriodsTab.COMPARISON_MEASURE_COMPARISON:
        return (
          <ComparisonMeasureComparison
            customMeasures={customMeasures}
            measureData={measureData}
            workPeriods={reversedWorkPeriods}
            handleMeasureClick={handleMeasureClick}
          />
        );
      case WorkPeriodsTab.COMPARISON_MEASURES_OVER_TIME:
        return (
          <ComparisonMeasuresOverTime
            customMeasures={customMeasures}
            measureData={measureData}
            workPeriods={reversedWorkPeriods}
            targets={availableTargets ?? {}}
            handleMeasureClick={handleMeasureClick}
          />
        );
    }
  }, [selectedTab, customMeasures, measureData, reversedWorkPeriods, availableTargets]);

  if (selectedWorkPeriods.length === 0 && !isFetching && !hasWorkPeriods) {
    return <NoWorkPeriodSelected />;
  }

  return (
    <ComparisonScope>
      <BrandedLoadingOverlay visible={isLoading} variant="colored" scoped />
      {mainComponent}
    </ComparisonScope>
  );
}

const ComparisonScope = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
`;
